/**
 * **A service's plans and fees** — the record's *Plans and fees* part, split
 * out of `ServiceDetail.tsx` when C25 took that file past 500 lines (ADR-0033,
 * Consequences: a file over 500 lines is split along its parts as it is
 * redrawn).
 *
 * Each live plan shows its price, its state when it is not active, and *Not on
 * your website* when the firm keeps it off its site; *Set a fee* / *Change
 * fee* and *Price on request* are the quick ways to its fee (Wave 15F), *Edit*
 * opens the whole plan in `PlanDialog`, and `MoveButtons` deactivate, activate
 * and delete it (C25, Wave 27D, T-1.31; ADR-0054 §1–§5). A deleted plan waits
 * under *Show deleted plans* to be restored — inactive.
 */

import { Badge, Button, Dialog } from '@fiveox/ui'
import { EyeOff, Plus, Trash2, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, RecordSection, StatusBadge } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type Plan, type Service, type Vocabulary, planShown, priceLabel, services } from '../../lib/api/services'
import { type CatalogueMoves, MoveButtons } from './moves'
import { PlanDialog, type PriceForm, PriceFields, priceForm, pricingBody } from './PlanDialog'

/** Set or change one plan's fee — the quick way, beside the plan's own
 *  dialog. Its fields are `PlanDialog`'s `PriceFields`, so both draw one set. */
function FeeDialog({
  plan,
  recurring,
  vocabulary,
  onClose,
  onSave,
}: {
  plan: Plan | null
  recurring: boolean
  vocabulary: Vocabulary | null
  onClose: () => void
  onSave: (planId: string, form: PriceForm) => Promise<string | null>
}) {
  const [form, setForm] = useState<PriceForm | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm(plan ? priceForm(plan) : null)
    setError(null)
  }, [plan])

  const save = async () => {
    if (!plan || !form) return
    setBusy(true)
    const failed = await onSave(plan.id, form)
    setBusy(false)
    if (failed) setError(failed)
    else onClose()
  }

  return (
    <Dialog
      open={plan !== null && form !== null}
      onClose={onClose}
      title={plan ? `The fee for ${plan.name}` : 'The fee'}
      error={error}
      width="lg"
    >
      {form && (
        <div className={FORM_GRID}>
          <PriceFields form={form} onChange={setForm} recurring={recurring} vocabulary={vocabulary} idBase="fee" />
        </div>
      )}
      <DialogActions>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} loading={busy}>
          Save fee
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function ServicePlans({
  service,
  editable,
  vocabulary,
  moves,
  onSaved,
  onError,
}: {
  service: Service
  editable: boolean
  vocabulary: Vocabulary | null
  moves: CatalogueMoves
  /** Reload the record, then say what happened. */
  onSaved: (sentence: string) => Promise<void>
  onError: (sentence: string) => void
}) {
  const [feePlan, setFeePlan] = useState<Plan | null>(null)
  const [planDialog, setPlanDialog] = useState<{ open: boolean; plan: Plan | null }>({ open: false, plan: null })
  const [showDeleted, setShowDeleted] = useState(false)

  const allPlans = service.plans ?? []
  const plans = allPlans.filter((plan) => plan.status !== 'deleted')
  const deletedPlans = allPlans.filter((plan) => plan.status === 'deleted')

  /** Returns the refusal to show in the dialog, or `null` when saved. */
  const savePrice = async (planId: string, form: PriceForm): Promise<string | null> => {
    try {
      await services.updatePlan(planId, { pricing: pricingBody(form) })
    } catch (err) {
      return err instanceof ApiError ? err.message : 'That price could not be saved.'
    }
    await onSaved('Saved.')
    return null
  }

  const clearPrice = async (plan: Plan) => {
    try {
      await services.updatePlan(plan.id, { clear_pricing: true })
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That price could not be cleared.')
      return
    }
    await onSaved(`“${plan.name}” is price on request.`)
  }

  return (
    <>
      <RecordSection
        id="service-plans"
        icon={<Wallet size={18} aria-hidden />}
        title="Plans and fees"
        description="A plan is a variant of this service — Basic, Standard, Premium — and the fee belongs to the plan."
        actions={
          editable ? (
            <Button variant="outline" size="sm" onClick={() => setPlanDialog({ open: true, plan: null })}>
              <Plus size={16} aria-hidden /> Add a plan
            </Button>
          ) : undefined
        }
      >
        {plans.length === 0 ? (
          <p className="text-muted-foreground text-sm">No plans yet.</p>
        ) : (
          <ul className="divide-border divide-y">
            {plans.map((plan) => (
              <li
                key={plan.id}
                data-plan={plan.id}
                className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-foreground flex flex-wrap items-center gap-2 font-medium">
                    {plan.name}
                    {plan.status !== 'active' && <StatusBadge kind="catalogue" status={plan.status} />}
                    {!planShown(plan) && (
                      <Badge tone="neutral" size="sm">
                        <EyeOff size={12} aria-hidden /> Not on your website
                      </Badge>
                    )}
                  </p>
                  {plan.description && <p className="text-muted-foreground text-sm">{plan.description}</p>}
                  <p className={plan.priced ? 'text-foreground text-sm' : 'text-muted-foreground text-sm'}>
                    {priceLabel(plan)}
                  </p>
                </div>
                {editable && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setFeePlan(plan)}>
                      {plan.priced ? 'Change fee' : 'Set a fee'}
                    </Button>
                    {plan.priced && (
                      <Button variant="ghost" size="sm" onClick={() => void clearPrice(plan)}>
                        Price on request
                      </Button>
                    )}
                    <MoveButtons
                      target={{ kind: 'plan', id: plan.id, name: plan.name, status: plan.status }}
                      moves={moves}
                      edit={() => setPlanDialog({ open: true, plan })}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {deletedPlans.length > 0 && (
          <div className="border-border mt-4 border-t pt-3">
            <Button variant="ghost" size="sm" onClick={() => setShowDeleted((v) => !v)} aria-expanded={showDeleted}>
              <Trash2 size={14} aria-hidden /> {showDeleted ? 'Hide' : 'Show'} deleted plans ({deletedPlans.length})
            </Button>
            {showDeleted && (
              <ul className="divide-border mt-2 divide-y" data-deleted-plans="">
                {deletedPlans.map((plan) => (
                  <li key={plan.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm font-medium">
                        {plan.name} <StatusBadge kind="catalogue" status={plan.status} />
                      </p>
                      <p className="text-muted-foreground text-sm">{priceLabel(plan)}</p>
                    </div>
                    {editable && (
                      <MoveButtons
                        target={{ kind: 'plan', id: plan.id, name: plan.name, status: plan.status }}
                        moves={moves}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </RecordSection>

      <FeeDialog
        plan={feePlan}
        recurring={service.is_recurring}
        vocabulary={vocabulary}
        onClose={() => setFeePlan(null)}
        onSave={savePrice}
      />
      <PlanDialog
        open={planDialog.open}
        serviceId={service.id}
        plan={planDialog.plan}
        recurring={service.is_recurring}
        vocabulary={vocabulary}
        onClose={() => setPlanDialog({ open: false, plan: null })}
        onSaved={onSaved}
      />
    </>
  )
}
